/* @flow */
import * as React from 'react';
import _ from 'lodash';

import adminBillingOverviewQuery from 'admin/queries/adminBillingOverviewQuery.gql';
import assertions from 'lib/assertions';
import Button from 'components/ButtonDeprecated';
import Columns2 from 'components/Columns2';
import FormElement from 'components/FormElement';
import { graphql } from 'react-apollo';
import gql from 'graphql-tag';
import Input from 'components/Input';
import InputValueConstants from 'constants/InputValueConstants';
import invariant from 'invariant';
import PhoneNumberMaskedInput from 'components/PhoneNumberMaskedInput';
import Text from 'components/Text';
import TitleHeader from 'components/TitleHeader';
import ToastActions from 'actions/ToastActions';

import type { BillingContact } from 'admin/components/AdminBillingOverview';

type Props = {
  billingContact: ?BillingContact,
  orgId: number,
  updateBillingContact: (
    orgId: number,
    firstName: string,
    lastName: string,
    email: string,
    phoneNumber: string
  ) => Promise<*>,
};

type State = {
  billingContact: ?BillingContact,
  submitting: boolean,
};

class AdminBillingContactForm extends React.Component<Props, State> {
  state: State = {
    billingContact: this.props.billingContact,
    submitting: false,
  };

  submit = (event: SyntheticEvent<>) => {
    event.preventDefault();

    this.setState({ submitting: true });
    invariant(
      this.state.billingContact,
      'AdminBillingContactForm tried to do something with billingContact but it was null!'
    );

    const { updateBillingContact, orgId } = this.props;
    const { firstName, lastName, email, phoneNumber } = this.state.billingContact;

    updateBillingContact(orgId, firstName, lastName, email, phoneNumber)
      .then(result => {
        if (result.error) {
          this.setState({ submitting: false });
          const { fieldErrors } = result.error;
          const errorMessage =
            fieldErrors && fieldErrors.length
              ? result.error.fieldErrors.map(f => f.message).join(', ')
              : result.error.message;
          ToastActions.showErrorToast(errorMessage);
          console.debug(result.error);
          console.error('AdminBillingContactForm - could not save form.');
        } else {
          this.setState({ submitting: false });
          ToastActions.showSuccessToast('Billing contact updated');
        }
      })
      .catch(e => {
        ToastActions.showErrorToast('Oops, something went wrong saving the quote request');
        console.debug(e, e && e.stack);
        console.error('AdminBillingContactForm - hard fail - could not save form');
        this.setState({ submitting: false });
      });
  };

  canSubmit = () => {
    const { billingContact } = this.state;

    if (billingContact) {
      return _.every([
        billingContact.firstName,
        billingContact.lastName,
        billingContact.email,
        billingContact.phoneNumber,
      ]);
    } else {
      return false;
    }
  };

  setBillingContactField = (name: string, value: *) => {
    this.setState({ billingContact: { ...this.state.billingContact, [name]: value } });
  };

  render() {
    const { billingContact } = this.props;
    const { submitting } = this.state;

    return (
      <section>
        <TitleHeader textAlign="left" title="Billing contact" />
        <Text size="medium" element="p">
          This person will receive invoices, receipts, and all information related to billing.
        </Text>
        <form onSubmit={this.submit}>
          <Columns2>
            <FormElement
              label="First name"
              validate={e => {
                assertions.lengthLesserThanOrEqualTo(
                  e.target.value,
                  InputValueConstants.FIRST_NAME_LENGTH
                );
                assertions.notEmpty(e.target.value);
              }}
              onValidChange={e => this.setBillingContactField('firstName', e.target.value)}
              onInvalidChange={() => this.setBillingContactField('firstName', undefined)}
              element={Input}
              elementProps={{
                defaultValue: billingContact && billingContact.firstName,
                autoComplete: 'given-name',
                name: 'fname',
              }}
            />
            <FormElement
              label="Last name"
              validate={e => {
                assertions.lengthLesserThanOrEqualTo(
                  e.target.value,
                  InputValueConstants.LAST_NAME_LENGTH
                );
                assertions.notEmpty(e.target.value);
              }}
              onValidChange={e => this.setBillingContactField('lastName', e.target.value)}
              onInvalidChange={() => this.setBillingContactField('lastName', undefined)}
              element={Input}
              elementProps={{
                defaultValue: billingContact && billingContact.lastName,
                autoComplete: 'family-name',
                name: 'lname',
              }}
            />
          </Columns2>
          <Columns2>
            <FormElement
              label="Email address"
              validate={e => {
                assertions.email(e.target.value);
                assertions.notEmpty(e.target.value);
              }}
              onValidChange={e => this.setBillingContactField('email', e.target.value)}
              onInvalidChange={() => this.setBillingContactField('email', undefined)}
              element={Input}
              elementProps={{
                defaultValue: billingContact && billingContact.email,
                autoComplete: 'email',
                type: 'email',
              }}
            />
            <FormElement
              label="Phone number"
              validate={e => {
                assertions.number(e.raw, 'Invalid phone number.');
                assertions.completePhoneNumber(e.raw, 'Invalid phone number.');
              }}
              onValidChange={e => this.setBillingContactField('phoneNumber', e.raw)}
              onInvalidChange={() => this.setBillingContactField('phoneNumber', undefined)}
              element={PhoneNumberMaskedInput}
              elementProps={{
                defaultValue: billingContact && billingContact.phoneNumber,
                autoComplete: 'tel',
                type: 'tel',
              }}
            />
          </Columns2>
          <Button size="small" type="submit" disabled={!this.canSubmit()} loading={submitting}>
            Save
          </Button>
        </form>
      </section>
    );
  }
}

const withCreateBillingContact = graphql(
  gql`
    mutation updateBillingContact($input: UpdateBillingContactInput!) {
      updateBillingContact(input: $input) {
        error {
          message
          code
          fieldErrors {
            field
            message
          }
        }
      }
    }
  `,
  {
    props: ({ mutate }) => ({
      updateBillingContact(
        orgId: number,
        firstName: string,
        lastName: string,
        email: string,
        phoneNumber: string
      ) {
        const input = { orgId, firstName, lastName, email, phoneNumber };
        return mutate({
          variables: { input },
          update: (
            store,
            {
              data: {
                updateBillingContact: { error },
              },
            }
          ) => {
            const currentData = store.readQuery({
              query: adminBillingOverviewQuery,
              variables: { organizationId: [orgId] },
            });

            if (error) {
              return;
            }

            currentData.organizations[0].billingContact = {
              firstName,
              lastName,
              email,
              phoneNumber,
            };

            store.writeQuery({ query: adminBillingOverviewQuery, data: currentData });
          },
        });
      },
    }),
  }
);

export { AdminBillingContactForm };
export default withCreateBillingContact(AdminBillingContactForm);
